import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { ICardSectionColumn } from '../../interfaces/card-section.interface';
import { BoardService } from '../../services/board.service';

export interface AvailableField {
  key: string;
  title: string;
  type: string;
  selected: boolean;
  translate?: boolean;
  readonly?: boolean;
  options?: string[];
}

export interface SelectFieldsDialogData {
  existingFields: ICardSectionColumn[];
  customFields?: any[];
  funnelId?: string;
  entityType?: string;
}

@Component({
  selector: 'app-select-fields',
  templateUrl: './select-fields.component.html',
  styleUrls: ['./select-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SelectFieldsComponent implements OnInit {
  public availableFields: AvailableField[] = [
    { key: 'phase', title: 'phase', type: 'select', selected: false, translate: true },
    { key: 'price', title: 'price', type: 'price', selected: false, translate: true },
    { key: 'phone', title: 'phone', type: 'text', selected: false, translate: true },
    { key: 'email', title: 'email', type: 'text', selected: false, translate: true },
    { key: 'assigned_id', title: 'responsible', type: 'select', selected: false, translate: true },
    { key: 'created_at', title: 'created_at', type: 'date', selected: false, translate: true, readonly: true },
    { key: 'updated_at', title: 'updated_at', type: 'date', selected: false, translate: true, readonly: true },
  ];
  public creatingCustomField = false;
  public savingCustomField = false;
  public customFieldError = '';
  public readonly customFieldName = new FormControl('', { nonNullable: true });
  public readonly customFieldKey = new FormControl('', { nonNullable: true });
  public readonly customFieldType = new FormControl('text', { nonNullable: true });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SelectFieldsDialogData,
    private readonly dialogRef: MatDialogRef<SelectFieldsComponent>,
    private readonly boardService: BoardService,
  ) {}

  ngOnInit(): void {
    if (this.data.customFields && this.data.customFields.length > 0) {
      const customFieldsList = this.data.customFields
        .map((field) => this.toAvailableField(field))
        .filter((field): field is AvailableField => !!field);
      const existingKeys = new Set(this.availableFields.map((field) => field.key));
      this.availableFields = [
        ...this.availableFields,
        ...customFieldsList.filter((field) => !existingKeys.has(field.key)),
      ];
    }

    for (const existingField of this.data.existingFields || []) {
      const availableField = this.availableFields.find((field) => field.key === existingField.key);
      if (availableField) {
        Object.assign(availableField, existingField, { selected: true });
      } else {
        this.availableFields.push({ ...existingField, selected: true });
      }
    }
  }

  toggleField(field: AvailableField): void {
    field.selected = !field.selected;
  }

  openCustomFieldForm(): void {
    this.creatingCustomField = true;
    this.customFieldError = '';
  }

  cancelCustomFieldForm(): void {
    if (this.savingCustomField) return;

    this.creatingCustomField = false;
    this.customFieldError = '';
    this.customFieldName.reset();
    this.customFieldKey.reset();
    this.customFieldType.setValue('text');
  }

  createCustomField(): void {
    const funnelId = this.data.funnelId;
    const name = this.customFieldName.value.trim();
    const rawKey = this.customFieldKey.value.trim();
    const type = this.customFieldType.value;
    const key = rawKey.startsWith('x_') ? rawKey : `x_${rawKey}`;

    if (!funnelId || !name || !rawKey || this.savingCustomField) {
      this.customFieldError = 'Заповніть назву та ключ поля';
      return;
    }

    if (this.availableFields.some((field) => field.key === key)) {
      this.customFieldError = 'Поле з таким ключем вже існує';
      return;
    }

    this.savingCustomField = true;
    this.customFieldError = '';
    this.boardService
      .createField({
        name,
        identifier: key,
        entity_id: funnelId,
        entity_type: this.data.entityType,
        input_type: type,
        data_type: type === 'checkbox' ? 'boolean' : 'varchar',
        options: [],
      })
      .pipe(finalize(() => (this.savingCustomField = false)))
      .subscribe({
        next: (field) => {
          const availableField = this.toAvailableField(field);
          if (!availableField) {
            this.customFieldError = 'Не вдалося створити поле';
            return;
          }

          availableField.selected = true;
          this.availableFields.push(availableField);
          this.data.customFields?.push(field);
          this.creatingCustomField = false;
          this.customFieldName.reset();
          this.customFieldKey.reset();
          this.customFieldType.setValue('text');
        },
        error: () => (this.customFieldError = 'Не вдалося створити поле'),
      });
  }

  private toAvailableField(field: any): AvailableField | null {
    const key = field?.key || field?.identifier;
    if (!key) return null;

    return {
      key,
      title: field?.name || field?.title || key,
      type: field?.type || field?.input_type || 'text',
      selected: false,
      options: field?.options,
      readonly: field?.readonly ?? field?.is_readonly,
    };
  }

  onConfirm(): void {
    const selectedFields = this.availableFields
      .filter((f) => f.selected)
      .map((f) => ({
        key: f.key,
        title: f.title,
        type: f.type,
        options: f.options,
        readonly: f.readonly,
        translate: f.translate,
      }));
    this.dialogRef.close(selectedFields);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
